<template>
  <div class="creator-suite-hero">
    <div class="creator-suite-hero__header">
      <h1 class="global-h1 creator-suite-hero__title">Creator Suite</h1>
      <p class="creator-suite-hero__subtitle">Everything you need to generate fresh, authentic content from creators, locals, and visitors.</p>
    </div>

    <!-- Toggle Pills -->
    <div class="creator-suite-hero__toggle-pills">
      <button
        :class="{
          'toggle-pill': true,
          'toggle-pill--active': activeView === 'creator-visits',
        }"
        @click="activeView = 'creator-visits'"
      >
        <IconEmbedded name="users_2" :size="20" />
        <span class="toggle-pill__text">Creator Visits</span>
      </button>
      <button
        :class="{
          'toggle-pill': true,
          'toggle-pill--active': activeView === 'community-uploads',
        }"
        @click="activeView = 'community-uploads'"
      >
        <IconEmbedded name="image_2-5" :size="20" />
        <span class="toggle-pill__text">Community Uploads</span>
      </button>
    </div>

    <!-- Content Views -->
    <div class="creator-suite-hero__content">
      <!-- Creator Visits View -->
      <div v-if="activeView === 'creator-visits'" class="creator-suite-hero__view creator-visits-view">
        <ul class="creator-visits-view__avatars-row">
          <AvatarWithFallback
            class="creator-visits-view__avatar"
            v-for="(avatarUrl, index) in creatorAvatars"
            :key="`avatar-${index}`"
            :src="avatarUrl"
            :class="{ 'creator-visits-view__avatar--center': index === 2 }"
          />
        </ul>

        <div class="creator-visits-view__text-content">
          <h2 class="global-h2 creator-visits-view__title">Creator Visits</h2>
          <p class="creator-visits-view__description">The simplest, most effective way to work with vetted creators who showcase your destination with authentic, high-quality content.</p>
          <SrpButton class="creator-visits-view__cta-button" color="orange" style="box-shadow: 0 8px 30px -5px #ec563b" @click="$emit('learnMoreClicked')">
            Learn More
            <template #iconRight><IconEmbedded name="caret-right_4" :size="18" /></template>
          </SrpButton>
        </div>
      </div>

      <!-- Community Uploads View -->
      <div v-else class="creator-suite-hero__view community-uploads-view">
        <div class="community-uploads-view__image-section">
          <img class="community-uploads-view__image" src="https://cdn.shrpa.com/images/misc/integrations/PhotoWall-med.jpg" alt="Community photo uploads grid" />
        </div>

        <div class="community-uploads-view__text-content">
          <h2 class="global-h2 community-uploads-view__title">Community Uploads</h2>
          <p class="community-uploads-view__description">Collect photos and videos from locals and travelers through easy public upload pages shared via your website, social, or QR codes.</p>
          <SrpButton class="community-uploads-view__cta-button" color="orange" style="box-shadow: 0 8px 30px -5px #ec563b" @click="$emit('learnMoreClicked')">
            Learn More
            <template #iconRight><IconEmbedded name="caret-right_4" :size="18" /></template>
          </SrpButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import AvatarWithFallback from "@components/AvatarWithFallback.vue";

export default defineComponent({
  name: "CreatorSuiteHero",

  components: {
    SrpButton,
    IconEmbedded,
    AvatarWithFallback,
  },

  emits: ["learnMoreClicked"],

  setup() {
    const activeView = ref<"creator-visits" | "community-uploads">("creator-visits");

    const creatorAvatars = [
      "https://dev-cdn.shrpa.com/cms/images/profiles/47d7ee26-0e1f-46e6-ae3b-bea99cd45a01.jpg",
      "https://cdn.shrpa.com/cms/images/profiles/2543ba8d-5f3b-4c5f-8bce-d1ed65cec3b2.jpg",
      "https://dev-cdn.shrpa.com/cms/images/profiles/49b4002e-9982-44bd-bda2-158be5d5fbf8.jpg",
      "https://dev-cdn.shrpa.com/cms/images/profiles/c5c1d14a-9b81-4681-aad2-ea2f35a5a484.jpg",
      "https://cdn.shrpa.com/cms/images/profiles/e28e4bf1-2a3a-43b9-b752-cbb908ef9947.jpg",
    ];

    return {
      activeView,
      creatorAvatars,
    };
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
@import "@/scss/screen-size-ranges.scss";

// Creator Suite Hero ==========================================================
.creator-suite-hero {
  padding-top: 90px;
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__header {
    margin-bottom: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    font-size: 48px;
    margin-bottom: 15px;
    color: #102f41;
    font-weight: 700;
    text-align: center;

    @media (max-width: $tablet-max-width) {
      font-size: 36px;
    }
  }

  &__subtitle {
    max-width: 700px;
    margin: 0;
    color: rgba(91, 91, 91, 1);
    font: 17px/23px sans-serif;
    text-align: center;
  }

  &__toggle-pills {
    margin-bottom: 40px;
    display: flex;
    gap: 0;
    padding: 6px;
    border-radius: 100px;
    background: rgba(0, 0, 0, 0.06);
  }

  &__content {
    width: 100%;
    max-width: 1100px;
  }

  &__view {
    min-height: 400px;
  }
}

// Toggle Pill ================================================================
.toggle-pill {
  padding: 12px 28px;
  border: none;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  color: rgba(91, 91, 91, 1);
  font:
    600 16px/20px "Quicksand",
    sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    color: #102f41;
  }

  &--active {
    background: white;
    color: #102f41;
    box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.15);
  }

  &__text {
  }
}

// Creator Visits View ========================================================
.creator-visits-view {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__avatars-row {
    padding: 0;
    margin: 0 0 40px;
    list-style: none;
    display: flex;
    justify-content: center;
  }

  &__avatar {
    width: 108px !important;
    height: 108px;
    margin-right: -21px;
    transition: transform 0.3s ease;

    &--center {
      width: 140px !important;
      height: 140px;
      z-index: 10 !important;
      transform: scale(1.05);
    }

    &:nth-child(1) {
      z-index: 5;
    }
    &:nth-child(2) {
      z-index: 6;
    }
    &:nth-child(3) {
      z-index: 10;
    }
    &:nth-child(4) {
      z-index: 6;
    }
    &:nth-child(5) {
      z-index: 5;
    }

    :deep(.avatar-with-fallback__img-wrap) {
      outline: 4px white solid;
      border-radius: 1000px;
      outline-offset: -2px;
    }
  }

  &__text-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    margin-bottom: 18px;
    color: #102f41;
    font-weight: 600;
    text-align: center;
  }

  &__description {
    max-width: 650px;
    margin: 0 0 30px;
    color: rgba(91, 91, 91, 1);
    font: 17px/23px sans-serif;
    text-align: center;
  }

  &__cta-button {
  }
}

// Community Uploads View =====================================================
.community-uploads-view {
  display: flex;
  gap: 60px;
  align-items: center;

  &__image-section {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__image {
    width: 100%;
    max-width: 500px;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.25);
  }

  &__text-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__title {
    margin-bottom: 18px;
    color: #102f41;
    font-weight: 600;
  }

  &__description {
    margin: 0 0 30px;
    color: rgba(91, 91, 91, 1);
    font: 17px/23px sans-serif;
  }

  &__cta-button {
  }
}

// Responsive breakpoints =====================================================

// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}

// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}

// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .community-uploads-view {
    gap: 40px;

    &__image {
      max-width: 400px;
    }
  }
}

// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .creator-suite-hero {
    padding: 70px 30px;

    &__subtitle {
      padding: 0 20px;
    }
  }

  .community-uploads-view {
    flex-direction: column;
    gap: 40px;

    &__image {
      max-width: 100%;
    }

    &__text-content {
      align-items: center;
    }

    &__title {
      text-align: center;
    }

    &__description {
      text-align: center;
    }
  }
}

// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .creator-suite-hero {
    padding: 60px 30px;

    &__subtitle {
      padding: 0 20px;
    }
  }

  .creator-visits-view {
    &__avatar {
      width: 95px !important;
      height: 95px;

      &--center {
        width: 120px !important;
        height: 120px;
      }
    }
  }

  .community-uploads-view {
    flex-direction: column;
    gap: 40px;

    &__image {
      max-width: 100%;
    }

    &__text-content {
      align-items: center;
    }

    &__title {
      text-align: center;
    }

    &__description {
      text-align: center;
    }
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .creator-suite-hero {
    padding: 50px 18px;

    &__header {
      margin-bottom: 30px;
    }

    &__subtitle {
      font-size: 15px;
      line-height: 21px;
    }

    &__toggle-pills {
      margin-bottom: 40px;
    }
  }

  .toggle-pill {
    padding: 10px 20px;
    font-size: 14px;
    line-height: 18px;

    :deep(svg) {
      width: 18px;
      height: 18px;
    }

    &__text {
      text-align: left;
    }
  }

  .creator-visits-view {
    &__avatars-row {
      margin-bottom: 30px;
    }

    &__avatar {
      width: 75px !important;
      height: 75px;
      margin-right: -18px;

      &--center {
        width: 95px !important;
        height: 95px;
      }

      /*&:nth-child(4),
      &:nth-child(5) {
        display: none;
      }*/
    }

    &__description {
      font-size: 15px;
      line-height: 21px;
    }
  }

  .community-uploads-view {
    flex-direction: column;
    gap: 30px;

    &__image {
      max-width: 100%;
    }

    &__text-content {
      align-items: center;
    }

    &__title {
      text-align: center;
    }

    &__description {
      text-align: center;
      font-size: 15px;
      line-height: 21px;
    }
  }
}
</style>
